import { useEffect, useState } from "react";
import { ShieldWarning, SignIn, XCircle, LockKey, Key, MagnifyingGlass } from "@phosphor-icons/react";
import { api, errMsg } from "../lib/api";
import { StatCard, GlassCard, Loader, Empty, Table } from "./kit";

const REASONS = { wrong_password: "Wrong password", unknown_email: "Unknown email", locked: "Locked out", account_disabled: "Account disabled", school_disabled: "School disabled", email_unavailable: "Reset email not sent — email credits", email_error: "Reset email failed" };
const label = (e) => e.event === "password_reset" ? "Password reset" : e.event === "reset_requested" ? "Reset link requested" : e.ok ? "Signed in" : REASONS[e.reason] || "Failed";
const when = (iso) => new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

function SuspiciousIps({ ips }) {
  if (!ips.length) return null;
  return (
    <div className="mb-6 rounded-2xl border border-rose-400/30 bg-rose-500/10 p-4" data-testid="suspicious-ips">
      <div className="flex items-center gap-2 text-sm font-semibold text-rose-200"><ShieldWarning size={18} weight="fill" /> Suspicious activity in the last 24 hours</div>
      <ul className="mt-2 space-y-1 text-sm text-rose-100/90">
        {ips.map((i) => (
          <li key={i.ip} data-testid={`suspicious-ip-${i.ip}`}><span className="font-mono">{i.ip}</span> — {i.failed} failed sign-ins across {i.email_count} account{i.email_count === 1 ? "" : "s"} ({i.emails.join(", ")})</li>
        ))}
      </ul>
    </div>
  );
}

function EventsTable({ items }) {
  return (
    <Table testid="login-events-table">
      <thead><tr><th>When</th><th>Account</th><th>School</th><th>Result</th><th>IP address</th></tr></thead>
      <tbody>
        {items.map((e) => (
          <tr key={e.id} data-testid={`login-event-${e.id}`}>
            <td className="whitespace-nowrap text-zinc-400">{when(e.at)}</td>
            <td><div className="font-semibold text-white">{e.email}</div><div className="text-[11px] capitalize text-zinc-500">{[e.name, e.role].filter(Boolean).join(" · ") || "No such account"}</div></td>
            <td className="text-zinc-300">{e.school_name || (e.role === "superadmin" ? "Platform" : "—")}</td>
            <td><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${e.ok ? "bg-emerald-500/15 text-emerald-300" : "bg-rose-500/15 text-rose-300"}`}>{label(e)}</span></td>
            <td className="font-mono text-xs text-zinc-400">{e.ip || "—"}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

export function LoginHistory({ schools = [] }) {
  const [f, setF] = useState({ status: "all", q: "", school_id: "", page: 1 });
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/platform/login-events", { params: f }).then((r) => { setData(r.data); setErr(""); }).catch((e) => setErr(errMsg(e)));
    }, 250);
    return () => clearTimeout(t);
  }, [f]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value, page: 1 });
  const s = data?.summary;
  return (
    <section className="mt-14" data-testid="login-history">
      <h2 className="font-display text-2xl font-semibold text-white">Login history</h2>
      <p className="mt-1 mb-6 text-sm text-zinc-400">Every sign-in, failed attempt and password reset across all schools, kept for one year.</p>
      <div className="mb-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={SignIn} label="Sign-ins · 24h" value={s?.logins_24h ?? "—"} tone="green" testid="stat-logins-24h" />
        <StatCard icon={XCircle} label="Failed · 24h" value={s?.failed_24h ?? "—"} tone="rose" testid="stat-failed-24h" delay={0.05} />
        <StatCard icon={LockKey} label="Lockouts · 24h" value={s?.locked_24h ?? "—"} tone="orange" testid="stat-locked-24h" delay={0.1} />
        <StatCard icon={Key} label="Password resets · 24h" value={s?.resets_24h ?? "—"} testid="stat-resets-24h" delay={0.15} />
      </div>
      {s && <SuspiciousIps ips={s.suspicious_ips} />}
      <GlassCard className="p-4 sm:p-6">
        <div className="mb-4 flex flex-col gap-3 md:flex-row">
          <div className="relative flex-1"><MagnifyingGlass size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input className="field !pl-9" placeholder="Search email, name or IP" value={f.q} onChange={set("q")} data-testid="login-events-search" /></div>
          <select className="field md:!w-44" value={f.status} onChange={set("status")} data-testid="login-events-status">
            <option value="all">All results</option><option value="failed">Failed only</option><option value="success">Successful only</option>
          </select>
          <select className="field md:!w-56" value={f.school_id} onChange={set("school_id")} data-testid="login-events-school">
            <option value="">All schools</option>{schools.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        </div>
        {err ? <p className="text-sm text-rose-300">{err}</p> : !data ? <Loader /> : !data.items.length ? <Empty hint="No sign-in activity matches." /> : <EventsTable items={data.items} />}
        {data && data.pages > 1 && (
          <div className="mt-4 flex items-center justify-end gap-3 text-sm text-zinc-400">
            <button className="btn-ghost !py-1.5" disabled={f.page <= 1} onClick={() => setF({ ...f, page: f.page - 1 })} data-testid="login-events-prev">Previous</button>
            <span data-testid="login-events-page">Page {data.page} of {data.pages}</span>
            <button className="btn-ghost !py-1.5" disabled={f.page >= data.pages} onClick={() => setF({ ...f, page: f.page + 1 })} data-testid="login-events-next">Next</button>
          </div>
        )}
      </GlassCard>
    </section>
  );
}
